// Turns API failures into sentences for people. The engine's error code and
// request ID stay available (for support and debugging) but are never the
// headline.
import { ApiError } from "./api";
import { CONTACT_EMAIL } from "./site";

/** Messages for the codes people meet most, in plain words. */
const MESSAGES: Record<string, string> = {
  network_error: "We couldn't reach Game Banking Engine. Check your connection and try again.",
  internal_error: "Something went wrong on our side. Try again in a moment.",
  rate_limited: "Too many attempts in a short time. Wait a moment and try again.",
  unauthenticated: "Your sign-in has expired. Sign in again.",
  invalid_sign_in: "That email and password don't match. Check them and try again.",
  invalid_password: "Your current password isn't right.",
  email_taken: "An account with this email already exists. Sign in instead.",
  registration_closed: "Sign-ups are closed right now.",
  developer_suspended: `This account is suspended. Email ${CONTACT_EMAIL} if you think that's a mistake.`,
  game_suspended: "This game is suspended, so it can't be changed or used right now.",
  insufficient_scope: "This key doesn't have permission to do that.",
  email_not_verified: "Verify your email address first. Check your inbox for the link.",
  login_code_invalid: "That code isn't right. Check the newest email and try again.",
  login_code_expired: "This code has expired or was used. Sign in again to get a new one.",
  too_many_codes: "We've sent several codes already. Sign in again to start over.",
  plan_upgrade_required: "This is a Pro feature. Upgrade or start your free trial to use it.",
  trial_already_used: "You've already used your free trial. Upgrade to keep using Pro features.",
  already_on_plan: "You're already on a paid plan.",
  already_subscribed: "You already have a Pro subscription. Manage it from Billing.",
  no_billing_account: "You haven't subscribed yet, so there's no billing to manage.",
  billing_unavailable: "Online payment isn't available right now. Email us to upgrade.",
  billing_provider_error: "We couldn't reach our payment provider. Try again in a moment.",
  too_many_streams: "Live balances are open in too many tabs. Close one and try again.",
  verification_token_invalid: "This verification link isn't valid any more. Sign in to get a new one.",
  verification_token_expired: "This verification link has expired. Sign in to get a new one.",
  already_verified: "Your email is already verified.",
  email_send_failed: "We couldn't send the email just now. Try again in a minute.",
  insufficient_funds: "There isn't enough money available in the account.",
  limit_exceeded: "This would go over a limit set for the bank.",
  account_frozen: "The account is frozen, so money can't leave it.",
  account_closed: "The account is closed.",
  fee_changed: "The fees changed since they were quoted. Check the new amount and try again.",
  penalty_changed: "The early-withdrawal penalty changed since it was quoted. Check the new amount and try again.",
  idempotency_key_reused: "This request was already sent with different details. Start again.",
  request_in_progress: "The same request is still being processed. Wait a moment and try again.",
};

/** The engine's own message, as a sentence: "the card is blocked" → "The card is blocked." */
function sentence(message: string): string {
  const m = message.trim();
  if (!m) return "Something went wrong. Try again.";
  const s = m[0].toUpperCase() + m.slice(1);
  return /[.!?]$/.test(s) ? s : `${s}.`;
}

/** What to show a person for an error. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (MESSAGES[error.code]) return MESSAGES[error.code];
    if (error.status >= 500) return MESSAGES.internal_error;
    return sentence(error.message);
  }
  if (error instanceof Error) return sentence(error.message);
  return "Something went wrong. Try again.";
}

/** The code and request ID behind an error, for support. */
export function errorReference(error: unknown): { code?: string; requestId?: string } {
  if (!(error instanceof ApiError)) return {};
  return { code: error.code === "network_error" ? undefined : error.code, requestId: error.correlationId };
}
